import { __decorate } from "tslib";
import { LitElement, html } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import '../character-counter';
import '../typography/tg-body-two';
import '../typography/tg-label-two';
import styles from './styles';
/**
 * TODO: The prefix icon has to change color on hover and active. However, it does not look
 *       like it is easy to achieve easily. Hover can be hacked try the parent element, but
 *       the active state is more complicated.
 * TODO: Design dictates that input change from the filled state to the clean state on hover,
 *       which does not look well. Need to groom with Design Team again.
 */
/**
 * @slot prefix - Icon to show as the input prefix (amityeko-icon is expected).
 *                It only works in combination with the hasPrefixIcon property.
 *
 * @cssproperty --amityeko-input-max-width
 *
 * @event amityeko-change {{ value }} - Emitted when an alteration to the control's value
 *                                      is committed by the user.
 * @event amityeko-input {{ value }} - Emitted when the control receives input and its value
 *                                     changes.
 */
export default class AmityekoInput extends LitElement {
    constructor() {
        super(...arguments);
        this.type = 'text';
        this.name = '';
        this.value = '';
        this.placeholder = '';
        this.label = undefined;
        this.textSuffix = undefined;
        this.hasCharacterCounter = false;
        this.hasPrefixIcon = false;
        this.required = false;
        this.clearable = false;
        this.hasAsterisk = false;
        this.filled = false;
        this.disabled = false;
        this.currentCount = 0;
    }
    /**
     * When any value is passed to the rnr-input, and then we change it to an empty string
     * - it will clear the input. But if the previously set value was an empty string,
     * there would be no effect. The element does not see change, so it will not update
     * its internal value.
     * This is in the case when an element is not being destroyed. When part of the dialog is
     * being shown and hidden, input remains in the DOM, and the value is not being reset.
     * This method resets the value.
     *
     * When an element is destroyed, there is no problem.
     *
     * It probably can be done much more elegantly with the lifecycle.
     */
    /** Resets the data in the element. */
    reset() {
        this.value = '';
    }
    firstUpdated() {
        this.input = this.renderRoot.querySelector('#input');
    }
    updated() {
        if (this.hasCharacterCounter) {
            this._getCharacterCounterPadding();
        }
    }
    _getCharacterCounterPadding() {
        this.currentCount = this.input.value.length;
        if (this.suffixElem && this.clearable && this.currentCount) {
            // get the suffix width and added padding 40
            this.rightPadding = this.suffixElem.offsetWidth + 40 + 'px';
        }
        else if (this.suffixElem) {
            // get the suffix width and added padding 5
            this.rightPadding = this.suffixElem.offsetWidth + 5 + 'px';
        }
        else if (this.clearable && this.currentCount) {
            this.rightPadding = '40px';
        }
        else {
            this.rightPadding = '15px';
        }
    }
    _onChange(e) {
        this.value = e.target.value;
        emit(this, 'amityeko-change', { detail: { value: this.value } });
    }
    _onInput(e) {
        this.value = e.target.value;
        this.currentCount = this.value.length;
        emit(this, 'amityeko-input', { detail: { value: this.value } });
    }
    render() {
        return html `
      <div class="input-contianer">
        <sl-input
          id="input"
          class="input ${classMap({
            'input--with-counter': Boolean(this.currentCount && this.hasCharacterCounter),
            'input--with-prefix': this.hasPrefixIcon,
        })}"
          size="medium"
          no-spin-buttons
          .type=${this.type}
          .name=${this.name}
          .value=${this.value}
          .placeholder=${this.placeholder}
          minlength=${ifDefined(this.minlength)}
          maxlength=${ifDefined(this.maxlength)}
          min=${ifDefined(this.min)}
          max=${ifDefined(this.max)}
          ?clearable=${this.clearable}
          ?required=${this.required}
          ?filled=${this.filled}
          ?disabled=${this.disabled}
          @sl-change=${this._onChange}
          @sl-input=${this._onInput}
        >
          ${this.hasPrefixIcon ? html `<slot name="prefix" slot="prefix"></slot>` : null}
          ${this.textSuffix
            ? html `
                <amityeko-tg-body-two id="suffix" slot="suffix" class="suffix">
                  ${this.textSuffix}
                </amityeko-tg-body-two>
              `
            : null}
          ${this.label
            ? html `
                <amityeko-tg-label-two class="label" slot="label">
                  ${this.label} ${this.required && this.hasAsterisk ? '*' : null}
                </amityeko-tg-label-two>
              `
            : null}
        </sl-input>
        ${this.hasCharacterCounter && this.maxlength
            ? html `
              <amityeko-character-counter
                class="counter"
                style=${styleMap({
                '--private-right-padding': this.rightPadding,
            })}
                .currentCount=${this.currentCount}
                .totalCount=${this.maxlength}
              ></amityeko-character-counter>
            `
            : null}
      </div>
    `;
    }
}
AmityekoInput.styles = styles;
__decorate([
    query('#suffix')
], AmityekoInput.prototype, "suffixElem", void 0);
__decorate([
    property({ attribute: false, reflect: true })
], AmityekoInput.prototype, "input", void 0);
__decorate([
    property()
], AmityekoInput.prototype, "type", void 0);
__decorate([
    property()
], AmityekoInput.prototype, "name", void 0);
__decorate([
    property()
], AmityekoInput.prototype, "value", void 0);
__decorate([
    property()
], AmityekoInput.prototype, "placeholder", void 0);
__decorate([
    property()
], AmityekoInput.prototype, "label", void 0);
__decorate([
    property()
], AmityekoInput.prototype, "textSuffix", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoInput.prototype, "hasCharacterCounter", void 0);
__decorate([
    property({ type: Boolean, attribute: 'has-prefix-icon' })
], AmityekoInput.prototype, "hasPrefixIcon", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoInput.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoInput.prototype, "clearable", void 0);
__decorate([
    property({ type: Number })
], AmityekoInput.prototype, "minlength", void 0);
__decorate([
    property({ type: Number })
], AmityekoInput.prototype, "maxlength", void 0);
__decorate([
    property({ type: Number })
], AmityekoInput.prototype, "min", void 0);
__decorate([
    property({ type: Number })
], AmityekoInput.prototype, "max", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoInput.prototype, "hasAsterisk", void 0);
__decorate([
    property({ type: Boolean, attribute: 'filled' })
], AmityekoInput.prototype, "filled", void 0);
__decorate([
    property({ type: Boolean })
], AmityekoInput.prototype, "disabled", void 0);
__decorate([
    state()
], AmityekoInput.prototype, "currentCount", void 0);
__decorate([
    state()
], AmityekoInput.prototype, "rightPadding", void 0);
if (!customElements.get('amityeko-input')) {
    customElements.define('amityeko-input', AmityekoInput);
}
//# sourceMappingURL=index.js.map